/**
 * Tangle module - Extract code blocks from markdown into executable scripts
 *
 * This module ports the bash tangle.sh script to TypeScript for use in both
 * Node.js and browser environments.
 */

import type { TangleResult, TangleResultWithMeta, ScriptTemplateMeta } from './types.js';
import { extractTangleBlocks, groupBlocksByTarget } from './parser.js';
import { substituteVariables } from '../weave/variables.js';
import { composeScriptBlocks } from '../script-converters.js';

export { extractTangleBlocks, groupBlocksByTarget } from './parser.js';
export type * from './types.js';

/**
 * Regex to find variable placeholders
 */
const VAR_REGEX = /\{\{([A-Za-z_][A-Za-z0-9_]*)\}\}/g;

/**
 * Tangle markdown content - extract code blocks into executable scripts
 *
 * This is the main entry point for the tangle module.
 *
 * @param content - The markdown content to process
 * @returns TangleResult with scripts map and status
 */
export function tangle(content: string): TangleResult {
  try {
    // Step 1: Extract all tangle blocks
    const blocks = extractTangleBlocks(content);

    if (blocks.length === 0) {
      return {
        scripts: {},
        success: true,
      };
    }

    // Step 2: Group blocks by target filename
    const grouped = groupBlocksByTarget(blocks);

    // Step 3: Generate scripts
    const scripts: Record<string, string> = {};

    for (const [target, blockList] of grouped) {
      // Get content from each block
      const blockContents = blockList.map(b => b.content);

      // Concatenate blocks using converter for this target extension
      const concatenated = composeScriptBlocks(target, blockContents);

      // Build final script content
      const script = `${concatenated}\n`;

      scripts[target] = script;
    }

    return {
      scripts,
      success: true,
    };
  } catch (error) {
    return {
      scripts: {},
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Tangle and return just the script contents without shebang
 *
 * Useful for cases where you want to embed the code directly
 * without the bash header.
 *
 * @param content - The markdown content to process
 * @returns TangleResult with scripts map and status
 */
export function tangleRaw(content: string): TangleResult {
  try {
    const blocks = extractTangleBlocks(content);

    if (blocks.length === 0) {
      return {
        scripts: {},
        success: true,
      };
    }

    const grouped = groupBlocksByTarget(blocks);
    const scripts: Record<string, string> = {};

    for (const [target, blockList] of grouped) {
      const blockContents = blockList.map(b => b.content);
      const concatenated = composeScriptBlocks(target, blockContents);
      scripts[target] = concatenated;
    }

    return {
      scripts,
      success: true,
    };
  } catch (error) {
    return {
      scripts: {},
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Extract unique variable names from content
 */
function extractVariables(content: string): string[] {
  const variables: string[] = [];
  let match: RegExpExecArray | null;

  // Reset regex state
  VAR_REGEX.lastIndex = 0;

  while ((match = VAR_REGEX.exec(content)) !== null) {
    const varName = match[1];
    if (!variables.includes(varName)) {
      variables.push(varName);
    }
  }

  return variables;
}

/**
 * Tangle markdown content with template metadata
 *
 * This version returns additional metadata about each script including
 * the pre-substitution template and variables used.
 *
 * @param content - The markdown content to process (pre-substitution)
 * @returns TangleResultWithMeta with scripts map, metadata, and status
 */
export function tangleWithMeta(content: string): TangleResultWithMeta {
  try {
    // Step 1: Extract all tangle blocks
    const blocks = extractTangleBlocks(content);

    if (blocks.length === 0) {
      return {
        scripts: {},
        scriptMeta: [],
        success: true,
      };
    }

    // Step 2: Group blocks by target filename
    const grouped = groupBlocksByTarget(blocks);

    // Step 3: Generate scripts and metadata
    const scripts: Record<string, string> = {};
    const scriptMeta: ScriptTemplateMeta[] = [];

    for (const [target, blockList] of grouped) {
      // Get content from each block
      const blockContents = blockList.map(b => b.content);

      // Concatenate blocks using converter for this target extension
      const concatenated = composeScriptBlocks(target, blockContents);

      // Build final script content
      const scriptContent = `${concatenated}\n`;

      scripts[target] = scriptContent;

      // Extract variables from the concatenated template
      const variables = extractVariables(concatenated);

      // Store template for variable tracking
      scriptMeta.push({
        name: target,
        template: concatenated,
        variables,
      });
    }

    return {
      scripts,
      scriptMeta,
      success: true,
    };
  } catch (error) {
    return {
      scripts: {},
      scriptMeta: [],
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Options for tangleWithSubstitution
 */
export interface TangleSubstitutionOptions {
  /** Variables to substitute in both target names and script content */
  variables: Record<string, string>;
}

/**
 * Tangle markdown content with variable substitution in a single pass
 *
 * This function extracts tangle blocks and applies variable substitution to both:
 * - Target script names (e.g., {{INIT_SCRIPT}} -> 1.init.sh)
 * - Script content (e.g., {{HOSTNAME}} -> example.com)
 *
 * It returns both the substituted scripts AND the original templates, keyed by
 * the resolved script name. This eliminates the need for fragile position-based
 * matching between separate tangle passes.
 *
 * @param content - The markdown content with {{VAR}} placeholders
 * @param options - Options including variables for substitution
 * @returns TangleResultWithMeta with substituted scripts and template metadata
 */
export function tangleWithSubstitution(
  content: string,
  options: TangleSubstitutionOptions
): TangleResultWithMeta {
  const { variables } = options;

  try {
    // Step 1: Extract all tangle blocks (with {{VAR}} in targets and content)
    const blocks = extractTangleBlocks(content);

    if (blocks.length === 0) {
      return {
        scripts: {},
        scriptMeta: [],
        success: true,
      };
    }

    // Step 2: Group blocks by target filename (still has {{VAR}} placeholders)
    const grouped = groupBlocksByTarget(blocks);

    // Step 3: Generate scripts with substitution and preserve templates
    // Use Maps to merge blocks that resolve to the same target name
    const scriptTemplates: Map<string, string[]> = new Map();
    const scriptVariables: Map<string, string[]> = new Map();

    for (const [templateTarget, blockList] of grouped) {
      // Resolve the target name (e.g., {{INIT_SCRIPT}} -> 1.init.sh)
      const { result: resolvedTarget } = substituteVariables(templateTarget, variables);

      // Get content from each block (still has {{VAR}} placeholders)
      const blockContents = blockList.map(b => b.content);

      // Concatenate blocks using converter for this target extension
      const templateContent = composeScriptBlocks(resolvedTarget, blockContents);

      // Extract variables used in this script's template
      const usedVariables = extractVariables(templateContent);

      // Merge with existing entries for the same resolved target
      if (scriptTemplates.has(resolvedTarget)) {
        scriptTemplates.get(resolvedTarget)!.push(templateContent);
        // Merge variables, avoiding duplicates
        const existing = scriptVariables.get(resolvedTarget)!;
        for (const v of usedVariables) {
          if (!existing.includes(v)) {
            existing.push(v);
          }
        }
      } else {
        scriptTemplates.set(resolvedTarget, [templateContent]);
        scriptVariables.set(resolvedTarget, [...usedVariables]);
      }
    }

    // Step 4: Build final scripts and metadata from merged data
    const scripts: Record<string, string> = {};
    const scriptMeta: ScriptTemplateMeta[] = [];

    for (const [resolvedTarget, templates] of scriptTemplates) {
      // Merge templates using the converter for the resolved target extension
      const mergedTemplate = composeScriptBlocks(resolvedTarget, templates);

      // Apply variable substitution to get final script content
      const { result: substitutedContent } = substituteVariables(mergedTemplate, variables);

      // Build final script content
      const script = `${substitutedContent}\n`;

      scripts[resolvedTarget] = script;

      scriptMeta.push({
        name: resolvedTarget,
        template: mergedTemplate,
        variables: scriptVariables.get(resolvedTarget) || [],
      });
    }

    return {
      scripts,
      scriptMeta,
      success: true,
    };
  } catch (error) {
    return {
      scripts: {},
      scriptMeta: [],
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}
